import { useQuery } from "@tanstack/react-query";
import {
    fetchProtocolData,
    type ProtocolData,
    type Farm,
    type lockedStats,
    type ProtocolParam,
    type ProtocolStats,
} from "@/services";
import { QUERY_KEYS, QUERY_CONFIG } from "@/config";

export interface ProtocolStore {
    stats: ProtocolStats;
    farms: Farm[];
    params: ProtocolParam;
    isLoading: boolean;
    error: Error | null;
    getFarmByAddress: (address: string) => Farm | undefined;
    getLIUSDByMaturity: (maturity: number) => lockedStats | undefined;
    getLIUSDByAddress: (address: string) => lockedStats | undefined;
}

export const useProtocolStore = (): ProtocolStore => {
    const { data, isLoading, error } = useQuery<ProtocolData>({
        queryKey: QUERY_KEYS.PROTOCOL.DATA,
        queryFn: fetchProtocolData,
        ...QUERY_CONFIG.PROTOCOL,
    });

    const getFarmByAddress = (address: string): Farm | undefined => {
        if (!data) return undefined;
        return data.farms.find((farm) => farm.address.toLowerCase() === address.toLowerCase());
    };

    const getLIUSDByMaturity = (maturity: number): lockedStats | undefined => {
        if (!data) return undefined;
        return Object.values(data.stats.locked).find((liusd) => liusd.bucketMaturity === maturity);
    };

    const getLIUSDByAddress = (address: string): lockedStats | undefined => {
        if (!data) return undefined;
        return Object.values(data.stats.locked).find((liusd) => liusd.address.toLowerCase() === address.toLowerCase());
    };

    return {
        stats: data?.stats ?? {
            asset: {
                address: "",
                decimals: 0,
                name: "",
                totalTVLAsset: "0",
                totalTVLAssetNormalized: 0,
                totalLiquidAsset: "0",
                totalLiquidAssetNormalized: 0,
                totalIlliquidAsset: "0",
                totalIlliquidAssetNormalized: 0,
                pendingRedemptionsAsset: "0",
                pendingRedemptionsAssetNormalized: 0,
            },
            receipt: {
                name: "",
                address: "",
                decimals: 0,
                totalSupply: "0",
                totalSupplyNormalized: 0,
                totalStaked: "0",
                totalStakedNormalized: 0,
                totalLocked: "0",
                totalLockedNormalized: 0,
                totalUnwindingNormalized: 0,
                receiptExchangeRate: "0",
                receiptExchangeRateNormalized: 0,
            },
            staked: {
                name: "",
                address: "",
                decimals: 0,
                totalSupply: "0",
                totalSupplyNormalized: 0,
                exchangeRate: "0",
                exchangeRateNormalized: 0,
                lastWeekAPY: 0,
                average30dAPY: 0,
                average7dAPY: 0,
            },
            locked: {},
        },
        farms: data?.farms ?? [],
        params: data?.params ?? {
            illiquidTargetRatio: 0,
            reserveRatio: 0,
            liquidReturnMultiplier: 0,
            illiquidReturnMultiplier: 0,
            unwindingMultiplier: 0,
            bucketMultipliers: [],
            smootherRate: 0,
        },
        isLoading,
        error: error as Error | null,
        getFarmByAddress,
        getLIUSDByMaturity,
        getLIUSDByAddress,
    };
};
